import { useFlags } from '@atlaskit/flag';
import { LocationsApiService, SubLocationsApiService } from 'core/api';
import { ILocationDao } from 'core/api/types';
import { LocationTab, locationTabs } from 'core/constants/location-tab';
import { useDialog } from 'core/providers/DialogProvider';
import { createAddressString } from 'core/utilities/address-string-builder';
import dayjs from 'dayjs';
import { where } from 'firebase/firestore';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import SharedButton from 'shared/components/buttons/button';
import SharedTabButton from 'shared/components/buttons/tab-button/tab-button';
import PageHeader from 'shared/components/page-header/page-header';
import PageHeaderSkeleton from 'shared/components/page-header/page-header-skeleton';
import UpdateLocationDialog from './dialogs/update-location-dialog';
import ConfirmationDialog from 'modules/dialogs/confirmation-dialog';
import SharedTable from 'shared/components/table/table';
import { ISharedTableColumn, ISharedTableCustomCellTemplate } from 'shared/components/table/table-interface';
import { ISubLocationDao } from 'core/api/types/sub-locations.interface';
import AddEditSubLocationDialog from '../sub-locations/dialogs/add-edit-sub-location-dialog';
import { useDocument } from 'core/hooks/useDocument';
import { useCollection } from 'core/hooks/useCollection';
import { deleteItem } from 'core/utilities/api-wrapper';

const SubLocationsTableColumns: ISharedTableColumn[] = [
  { label: 'Sub Location', key: 'name', templateId: 'text', width: 15 },
  { label: 'Notes', key: 'notes', templateId: 'text', width: 35 },
  { label: 'Status', key: 'active', templateId: 'boolean', width: 10 },
  { label: '', key: 'action', templateId: 'action', width: 10 },
];

const LocationsItem = () => {
  const { uid } = useParams();
  const flags = useFlags();
  const dialog = useDialog();

  const [selectedTab, setSelectedTab] = useState<string>(LocationTab.SUB_LOCATIONS);

  const constraints = useMemo(() => [where('locationId', '==', uid)], [uid]);

  const { tableRows, loading: subLocationsLoading } = useCollection<ISubLocationDao>({
    apiService: SubLocationsApiService,
    constraints,
  });

  const { data: location, loading: locationLoading } = useDocument<ILocationDao>({
    apiService: LocationsApiService,
    uid,
    navigateTo: '/locations',
  });

  const actionCellTemplate = (subLocation: ISubLocationDao) => (
    <div className='w-full flex justify-end space-x-2'>
      <SharedButton
        onClick={() => dialog?.openDialog(<AddEditSubLocationDialog subLocation={subLocation} />)}
        type='button'
        appearance='subtle'
        spacing='compact'
        label='Update'
      />
      <SharedButton
        onClick={() =>
          dialog?.openDialog(
            <ConfirmationDialog
              title='Delete sub location'
              textContent='Are you sure you want to delete this sub location? This action cannot be undone.'
              action={() => deleteItem(SubLocationsApiService, subLocation, flags, dialog)}
              buttonLabel='Delete Sub Location'
            />
          )
        }
        type='button'
        appearance='subtle'
        spacing='compact'
        label='Delete'
      />
    </div>
  );

  const customTemplates: ISharedTableCustomCellTemplate[] = [
    {
      template: actionCellTemplate,
      id: 'action',
    },
  ];

  const getTabContent = (location: ILocationDao) => {
    switch (selectedTab) {
      case LocationTab.SUB_LOCATIONS:
        return (
          <>
            <div className='flex justify-end mb-2'>
              <SharedButton
                onClick={() => dialog?.openDialog(<AddEditSubLocationDialog location={location} />)}
                type='button'
                appearance='primary'
                label='Add Sub Location'
              />
            </div>
            <SharedTable
              columns={SubLocationsTableColumns}
              rows={tableRows}
              isLoading={subLocationsLoading}
              customTemplates={customTemplates}
            />
          </>
        );
    }
  };

  const LocationsItemDetails = ({ location }: { location: ILocationDao }) => {
    const LocationDetails = [
      {
        label: 'Location name',
        value: location.name,
        key: 'name',
      },
      {
        label: 'Address',
        value: createAddressString([...location.address.fullAddress.formatted_address, location.address.postcode]),
        key: 'address',
      },
      {
        label: 'Phone number',
        value: location.phoneNumber,
        key: 'phoneNumber',
      },
      {
        label: 'Created at',
        value: location.created.at ? dayjs(location.created.at.toDate()).format('HH:mm, DD/MM/YYYY') : 'Unknown',
        key: 'createdAt',
      },
      {
        label: 'Created by',
        value: location.created.by.fullName ?? 'Unknown',
        key: 'createdBy',
      },
    ];

    return (
      <div className='flex-grow-0 flex-shrink-0 basis-[360px] bg-white shadow-md rounded-md'>
        <div className='border-b p-4 flex justify-between items-center'>
          <p className='headline-06'>Details</p>
          <div className='flex space-x-2'>
            <SharedButton
              onClick={() => dialog?.openDialog(<UpdateLocationDialog location={location} />)}
              type='button'
              appearance='primary'
              label='Update'
            />
            <SharedButton
              onClick={() =>
                dialog?.openDialog(
                  <ConfirmationDialog
                    title='Delete location'
                    textContent='Are you sure you want to delete this location? This action cannot be undone.'
                    action={() => deleteItem(LocationsApiService, location, flags, dialog)}
                    buttonLabel='Delete Location'
                  />
                )
              }
              type='button'
              appearance='danger'
              label='Delete'
            />
          </div>
        </div>
        <div className='p-4'>
          {LocationDetails.map((section) => (
            <div key={section.key} className='mt-4 first:mt-0'>
              <p className='body-02 text-gray-500'>{section.label}</p>
              <p className='body-01 break-words'>{section.value}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return !location || locationLoading ? (
    <PageHeaderSkeleton actions={[]} subtitle={true} />
  ) : (
    <>
      <PageHeader title={`${location.name}`} showBack={true} />
      <div className='flex flex-row py-6'>
        <LocationsItemDetails location={location} />
        <div className='pl-6 w-full'>
          <div className='relative'>
            <div className='flex relative z-10'>
              {locationTabs.map((tab) => (
                <SharedTabButton key={tab.key} changeTab={setSelectedTab} tab={tab} currentTab={selectedTab} />
              ))}
            </div>
            <div className='absolute bottom-0 left-0 right-0 h-px bg-gray-200' />
          </div>
          <div className='mt-6'>{getTabContent(location)}</div>
        </div>
      </div>
    </>
  );
};

export default LocationsItem;
